import {
  getAppImageData,
  getAppImagesData,
  getAppImagesDataWithSizes,
} from '@components/appImage';
import { createElement, type ReactNode } from 'react';

import {
  DomainMedia,
  MOBILE_SDK_MEDIA_CONFIG,
  MOBILE_SDK_SCREEN_SIZE,
  MobileSdkMedia,
  RADAR_API_MEDIA_CONFIG,
  RadarApiMedia,
  ROUTING_FEATURE_MEDIA_CONFIG,
  ROUTING_FEATURE_MEDIA_SIZE,
  RoutingFeatureMedia,
} from './constants';

import { Building20 } from '@/shared/icons/20/Building20';
import { Bus20 } from '@/shared/icons/20/Bus20';
import { Car20 } from '@/shared/icons/20/Car20';
import { Cycle20 } from '@/shared/icons/20/Cycle20';
import { Delivery20 } from '@/shared/icons/20/Delivery20';
import { EmergencyServices20 } from '@/shared/icons/20/EmergencyServices20';
import { Pedestrian20 } from '@/shared/icons/20/Pedestrian20';
import { Scooter20 } from '@/shared/icons/20/Scooter20';
import getSignedUrl from '@/shared/lib/imgproxy/getSignedUrl';
import { Domain } from '@/shared/types';
import {
  generateResourceUrl,
  ResourceType,
} from '@/shared/utils/generateResourceUrl';

const ICON_COLOR = 'rgba(255, 255, 255, 0.64)';

const ROUTING_EXAMPLE_TYPES: Array<{ typeCode: string; icon: ReactNode }> = [
  {
    typeCode: '0',
    icon: createElement(Car20, { color: ICON_COLOR }),
  },
  {
    typeCode: '1',
    icon: createElement(Delivery20, { color: ICON_COLOR }),
  },
  {
    typeCode: '2',
    icon: createElement(Bus20, { color: ICON_COLOR }),
  },
  {
    typeCode: '3',
    icon: createElement(EmergencyServices20, {
      color: ICON_COLOR,
    }),
  },
  {
    typeCode: '4',
    icon: createElement(Pedestrian20, { color: ICON_COLOR }),
  },
  {
    typeCode: '5',
    icon: createElement(Cycle20, { color: ICON_COLOR }),
  },
  {
    typeCode: '6',
    icon: createElement(Scooter20, { color: ICON_COLOR }),
  },
  {
    typeCode: '7',
    icon: createElement(Building20, { color: ICON_COLOR }),
  },
];

export const makeRoutingExamples = (
  localization: Record<string, string>,
  count: number,
) =>
  ROUTING_EXAMPLE_TYPES.slice(0, count).map(({ typeCode, icon }) => {
    const titleId = `routesForAllTypes${typeCode}Type`;

    return { titleId, title: localization[titleId], typeCode, icon };
  });

export type RoutingExampleMedia = Record<string, DomainMedia>;

/** Атрибуты width/height у примеров маршрутов, как в вёрстке (759x528) */
export const ROUTING_EXAMPLE_MEDIA_SIZE = { width: 759, height: 528 };

const EXAMPLE_TYPES_CONFIG: Record<number, Record<Domain, string>> = {
  0: {
    ru: '/images/api_nav/map-examples/auto/ru.png',
    kz: '/images/api_nav/map-examples/auto/kz.png',
    com: '/images/api_nav/map-examples/auto/com.png',
    am: '/images/api_nav/map-examples/auto/am.png',
    ge: '/images/api_nav/map-examples/auto/ge.png',
    tj: '/images/api_nav/map-examples/auto/tj.png',
  },
  1: {
    ru: '/images/api_nav/map-examples/truck/ru.png',
    kz: '/images/api_nav/map-examples/truck/kz.png',
    com: '/images/api_nav/map-examples/truck/com.png',
    am: '/images/api_nav/map-examples/truck/am.png',
    ge: '/images/api_nav/map-examples/truck/ge.png',
    tj: '/images/api_nav/map-examples/truck/tj.png',
  },
  2: {
    ru: '/images/api_nav/map-examples/bus/ru.png',
    kz: '/images/api_nav/map-examples/bus/kz.png',
    com: '/images/api_nav/map-examples/bus/com.png',
    am: '/images/api_nav/map-examples/bus/am.png',
    ge: '/images/api_nav/map-examples/bus/ge.png',
    tj: '/images/api_nav/map-examples/bus/tj.png',
  },
  3: {
    ru: '/images/api_nav/map-examples/emergency/ru.png',
    kz: '/images/api_nav/map-examples/emergency/kz.png',
    com: '/images/api_nav/map-examples/emergency/com.png',
    am: '/images/api_nav/map-examples/emergency/am.png',
    ge: '/images/api_nav/map-examples/emergency/ge.png',
    tj: '/images/api_nav/map-examples/emergency/tj.png',
  },
  4: {
    ru: '/images/api_nav/map-examples/pedestrian/ru.png',
    kz: '/images/api_nav/map-examples/pedestrian/kz.png',
    com: '/images/api_nav/map-examples/pedestrian/com.png',
    am: '/images/api_nav/map-examples/pedestrian/am.png',
    ge: '/images/api_nav/map-examples/pedestrian/ge.png',
    tj: '/images/api_nav/map-examples/pedestrian/tj.png',
  },
  5: {
    ru: '/images/api_nav/map-examples/bicycle/ru.png',
    kz: '/images/api_nav/map-examples/bicycle/kz.png',
    com: '/images/api_nav/map-examples/bicycle/com.png',
    am: '/images/api_nav/map-examples/bicycle/am.png',
    ge: '/images/api_nav/map-examples/bicycle/ge.png',
    tj: '/images/api_nav/map-examples/bicycle/tj.png',
  },
  6: {
    ru: '/images/api_nav/map-examples/scooter/ru.png',
    kz: '/images/api_nav/map-examples/scooter/kz.png',
    com: '/images/api_nav/map-examples/scooter/com.png',
    am: '/images/api_nav/map-examples/scooter/am.png',
    ge: '/images/api_nav/map-examples/scooter/ge.png',
    tj: '/images/api_nav/map-examples/scooter/tj.png',
  },
  7: {
    ru: '/images/api_nav/map-examples/indoor/ru.gif',
    kz: 'videos/routing-kz.mp4',
    com: 'videos/routing-com.mp4',
    am: 'videos/api_nav/map-examples/am.mp4',
    ge: 'videos/routing-ge.mp4',
    tj: 'videos/routing-tj.mp4',
  },
};

/** Постеры роликов — первые кадры, по пути видео */
const VIDEO_POSTERS: Record<string, string> = {
  'videos/routing-kz.mp4': '/images/video-posters/routing-indoor-kz.png',
  'videos/routing-com.mp4': '/images/video-posters/routing-indoor-com.png',
  'videos/routing-ge.mp4': '/images/video-posters/routing-indoor-ge.png',
  'videos/routing-tj.mp4': '/images/video-posters/routing-indoor-tj.png',
};

const getType = (path: string): ResourceType => {
  const normalizedPath = path.toLowerCase();

  if (normalizedPath.endsWith('.gif')) {
    return 'gif';
  }

  return normalizedPath.endsWith('.mp4') ? 'video' : 'image';
};

/**
 * Готовит медиа по доменам: картинки — через AppImage с общим размером
 * рендера, gif/video — публичные URL как есть.
 */
const getDomainMedia = async (
  pathsByDomain: Record<Domain, string>,
  size: { width: number; height: number },
): Promise<DomainMedia> => {
  const entries = await Promise.all(
    (Object.entries(pathsByDomain) as [Domain, string][]).map(
      async ([domain, contentPath]) => {
        const type = getType(contentPath);

        if (type === 'image') {
          return [
            domain,
            { type, data: await getAppImageData(contentPath, size) },
          ] as const;
        }

        const src = await generateResourceUrl({ path: contentPath, type });

        if (type === 'gif') {
          return [domain, { type, src }] as const;
        }

        const posterPath = VIDEO_POSTERS[contentPath];
        const poster =
          posterPath &&
          (await getSignedUrl(posterPath, size.width, size.height));

        return [domain, { type, src, poster }] as const;
      },
    ),
  );

  return Object.fromEntries(entries) as DomainMedia;
};

const getMediaByKey = async (
  config: Record<string, Record<Domain, string>>,
  size: { width: number; height: number },
): Promise<Record<string, DomainMedia>> => {
  const entries = await Promise.all(
    Object.entries(config).map(
      async ([key, pathsByDomain]) =>
        [key, await getDomainMedia(pathsByDomain, size)] as const,
    ),
  );

  return Object.fromEntries(entries);
};

export const getRoutingExamplesMedia = (): Promise<RoutingExampleMedia> =>
  getMediaByKey(EXAMPLE_TYPES_CONFIG, ROUTING_EXAMPLE_MEDIA_SIZE);

export const getRoutingFeatureMedia = (): Promise<RoutingFeatureMedia> =>
  getMediaByKey(ROUTING_FEATURE_MEDIA_CONFIG, ROUTING_FEATURE_MEDIA_SIZE);

export const getRadarApiMedia = (): Promise<RadarApiMedia> =>
  getAppImagesDataWithSizes(RADAR_API_MEDIA_CONFIG);

export const getMobileSdkMedia = (): Promise<MobileSdkMedia> =>
  getAppImagesData(MOBILE_SDK_MEDIA_CONFIG, MOBILE_SDK_SCREEN_SIZE);
